/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { memo, ReactElement } from 'react';
import {
  IconButton,
  InputAdornment,
  OutlinedTextFieldProps,
  StandardTextFieldProps,
  TextField as MaterialUiTextField,
} from '@mui/material';

import { ISharedProps } from '../../tools/ISharedProps';
import { ISharedFormProps } from '../ISharedFormProps';
import classNames from 'classnames';
import { NeoTextFieldStyles } from './NeoTextField.styles';
import { Typography } from '~/UIKit/Text';
import { IKeyboardDatePickerPropsOptions } from '../IKeyboardDatePickerPropsOptions';
import Clear from '@mui/icons-material/Clear';
import { ChevronRight } from '@mui/icons-material';

export interface ITextFieldProps extends StandardTextFieldProps, ISharedProps, ISharedFormProps {
  newDesign?: boolean;
  icon?: ReactElement;
  options?: IKeyboardDatePickerPropsOptions;
  errorOutline?: boolean;
  inputFormat?: string;
}

export interface IOutlinedTextFieldProps
  extends OutlinedTextFieldProps,
    ISharedProps,
    ISharedFormProps {
  newDesign?: boolean;
  icon?: ReactElement;
  options?: IKeyboardDatePickerPropsOptions;
  errorOutline?: boolean;
  inputFormat?: string;
}

export const NeoTextField = memo((props: ITextFieldProps | IOutlinedTextFieldProps) => {
  const {
    InputProps,
    InputLabelProps,
    type,
    className,
    helperText,
    disabled,
    errorOutline,
    multiline,
    label,
    options,
    ...restOfProps
  } = props;

  const { classes } = NeoTextFieldStyles({
    withLabel: !!props.label,
    startAdornment: !!props.InputProps?.startAdornment,
    endAdornment: !!props.InputProps?.endAdornment,
    isText: props.type === 'text',
    isSelect: props.select,
    hasError: !!props.error || !!errorOutline,
  })();

  const newCommonProps = {
    color: 'primary' as 'primary',
    name: props.field?.name,
    onChange: props.field?.onChange,
    onBlur: props.field?.onBlur,
    value: props.field?.value,
    classes: {
      root: classNames(className, classes.textFieldContainer),
    },
    type: props.type,
    InputLabelProps: { classes: { root: classes.labelContainer }, ...InputLabelProps },
  };

  const onClear = () => {
    if (props.value) {
      props.value = null;
    }
  };

  if (type === 'number') {
    return (
      <MaterialUiTextField
        {...newCommonProps}
        InputProps={{
          ...InputProps,
          notched: false,
          classes: {
            input: classNames(classes.input, classes.outlinedInput),
            focused: classes.inputFocused,
            notchedOutline: classes.notchedOutline,
            root: classes.inputContainer,
            ...InputProps?.classes,
          },
        }}
        disabled={disabled}
        {...restOfProps}
        placeholder=""
        variant="outlined"
        label={<Typography className={classes.label}>{label}</Typography>}
      />
    );
  } else {
    return (
      <MaterialUiTextField
        {...newCommonProps}
        InputProps={{
          ...InputProps,
          classes: {
            input: multiline ? classes.textarea : classes.input,
            root: multiline ? classes.textareaContainer : classes.inputContainer,
            ...InputProps?.classes,
          },
        }}
        multiline={multiline}
        disabled={disabled}
        focused={!disabled}
        {...restOfProps}
        sx={{
          width: '300px',
          '& .MuiSelect-select span::before': {
            content: props?.placeholder?.length ? `"${props.placeholder}"` : '""',
          },
        }}
        label={
          !!label ? (
            <Typography
              className={`${classes.label} ${options?.labelBold ? classes.labelBold : ''}`}
            >
              {label}
            </Typography>
          ) : undefined
        }
      />
    );
  }
});
